import { trackButtonClicked } from '@/lib/analytics/scribe/button';
import { trackScreenViewed } from '@/lib/analytics/scribe/ui';
import { cn } from '@/lib/cn';
import { hasSeenTrialCompletionTooltipAtom } from '@heidi/commerce/data';
import {
  Spotlight,
  SpotlightAnchor,
  SpotlightArrow,
  SpotlightClose,
  SpotlightContent,
  SpotlightDescription,
  SpotlightHeader,
  SpotlightTitle,
} from '@heidi/design-system/spotlight';
import { ButtonAction, ScreenName } from '@heidi/runtime/analytics-vocabulary';
import { useTranslations } from '@heidi/runtime/i18n';
import { useAtom } from 'jotai';
import { PartyPopper } from 'lucide-react';
import { type PropsWithChildren, useCallback, useEffect, useRef } from 'react';

type TrialCompletionTooltipProps = PropsWithChildren<{
  /** True once every gamified trial task is finished. Drives the one-time celebration. */
  allTasksComplete: boolean;
  /** Number of free trial days granted, surfaced in the celebration copy. */
  trialDays: number;
  /**
   * Width utility for the anchor wrapper. Defaults to `w-fit` (content-width,
   * for the icon-only ribbon button). Pass `w-full` when wrapping a full-width
   * nav item so it isn't collapsed to content width.
   */
  anchorClassName?: string;
}>;

/**
 * One-time celebratory tooltip anchored to the gamified-trial sidebar button.
 * Auto-opens the first time all trial tasks are complete, then persists a
 * "seen" flag so it never reappears.
 */
export const TrialCompletionTooltip = ({
  allTasksComplete,
  trialDays,
  anchorClassName = 'w-fit',
  children,
}: TrialCompletionTooltipProps) => {
  const { t } = useTranslations();
  const [hasSeen, setHasSeen] = useAtom(hasSeenTrialCompletionTooltipAtom);
  const hasTrackedViewRef = useRef(false);

  const shouldShow = allTasksComplete && !hasSeen;

  useEffect(() => {
    if (shouldShow && !hasTrackedViewRef.current) {
      trackScreenViewed({
        screen_name: ScreenName.TrialExtensionCompletionTooltip,
      });
      hasTrackedViewRef.current = true;
    }
  }, [shouldShow]);

  const handleDismiss = useCallback(() => {
    setHasSeen(true);
    trackButtonClicked({
      screenName: ScreenName.TrialExtensionCompletionTooltip,
      action: ButtonAction.TrialExtensionCompletionTooltipDismissed,
      label: 'Dismiss',
    });
  }, [setHasSeen]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) handleDismiss();
    },
    [handleDismiss]
  );

  return (
    <Spotlight open={shouldShow} onOpenChange={handleOpenChange}>
      <SpotlightAnchor asChild>
        <div className={cn(anchorClassName)}>{children}</div>
      </SpotlightAnchor>
      <SpotlightContent
        side="right"
        align="center"
        sideOffset={12}
        data-testid="trial-completion-spotlight"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <SpotlightArrow />
        <SpotlightHeader icon={<PartyPopper aria-hidden />}>
          <SpotlightTitle>{t('trialExtension.completionTooltip.title')}</SpotlightTitle>
          <SpotlightClose aria-label={t('trialExtension.completionTooltip.close')} />
        </SpotlightHeader>
        <SpotlightDescription>
          {t('trialExtension.completionTooltip.description', { days: trialDays })}
        </SpotlightDescription>
      </SpotlightContent>
    </Spotlight>
  );
};
